import React, {useState} from 'react';

import * as BenchlingUtil from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  PROCEDURE_CREATE_STARTED_EVENT,
  PROCEDURE_CREATE_ENDED_EVENT,
  composeProcedureTypeMetadata,
} from 'performance/processProceduresEvents';
import {getNewProcedureFlowchart} from 'procedures/design/flowchartManagementUtils';
import {isNameEmpty} from 'procedures/design/validation/validateProcedure';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import {ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {BasicEvent} from 'util/Types';

import {TemplateCollectionGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';

export interface CreateProcedureModalProps {
  onRequestClose: () => void;
  onCreateProcedure: (procedure: {
    flowchart: ProcedureFlowchart;
    name: string;
    templateCollectionId: string;
  }) => Promise<string>;
  initialTemplateCollection: TemplateCollectionGraphQL;
  procedureType: ProcedureType;
}

export default function CreateProcedureModal(props: CreateProcedureModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [templateCollection, setTemplateCollection] = useState(props.initialTemplateCollection);
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
  });

  const trimmedName = name.trim();
  const isNameBlank = isNameEmpty(trimmedName);

  function manageCollections(e: BasicEvent): void {
    const url = TemplateCollectionDAO.collectionsURL();
    BenchlingUtil.navigate(e, url);
    props.onRequestClose();
  }

  function isSubmitEnabled(): boolean {
    const hasTemplateCollection = templateCollection != null;
    return hasTemplateCollection && !isNameBlank;
  }

  return (
    <Modal
      title={`Create ${procedureString}`}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        logProfilerTimingEvent(PROCEDURE_CREATE_STARTED_EVENT);

        await withLoadingScreen(
          props
            .onCreateProcedure({
              name: trimmedName,
              templateCollectionId: templateCollection.id,
              flowchart: getNewProcedureFlowchart(props.procedureType),
            })
            .then((id) => {
              navigateToURL(ProcedureDAO.procedurePageUrl(id));
            }),
          `Creating new ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
            stringCase: StringCase.LOWER,
          })}...`
        );
        props.onRequestClose();
        const metadata = composeProcedureTypeMetadata(props.procedureType);
        logProfilerTimingEvent(PROCEDURE_CREATE_ENDED_EVENT, metadata);
      }}
      buttons={[
        {
          text: 'Create',
          submit: true,
          disabled: !isSubmitEnabled(),
          tooltipTitle: isNameBlank ? 'Procedure names must be non-empty.' : null,
        },
        {text: 'Manage Collections', onClick: manageCollections, asLink: true, pullLeft: true},
      ]}
      dataTestAttrs={{component: 'CreateProcedureModal', element: 'modal'}}
    >
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'CreateProcedureModal', element: 'name'}}>
            <Label>Name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(clampEntryName(e.target.value));
              }}
              autoFocus={true}
            />
          </FormGroup>
        </Col>
        <Col sm={5}>
          <Label>Template Collection</Label>
          <TemplateCollectionSelector
            value={templateCollection}
            onChange={(collection) => {
              setTemplateCollection(collection);
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
}
